<template>
  <b-overlay :show="isLoading" fixed rounded="lg">
    <template #overlay>
      <AppSpinner class="text-secondary mx-auto" size="8"/>
    </template>
    <b-form-group id="phone" :label="$t($getConst.pages.personalDetails.phone_number)" label-for="phone-input">
      <VuePhoneNumberInput
        id="phone-input"
        v-model="$v.phoneBasicNumber.$model"
        :only-countries="allowedCountries"
        :loader="isLoadingCountries"
        :disabled="isLoading || isLoadingCountries"
        class="text-capitalize"
        :class="{ 'is-invalid': $v.form.phone.$error }"
        :placeholder="$t(this.$getConst.pages.personalDetails.phone_number)"
        :border-radius="8"
        :translations="translation"
        :clearable="true"
        no-use-browser-locale
        size="lg"
        error-color	= "#e55353"
        valid-color	= "#0051f6"
        color	= "#0051f6"
        ref="PhoneNumberInput"
        @update="showPayload"
      />
      <AppFormFeedback :errors="$v.form.phone"/>
      <b-form-invalid-feedback v-if="!$v.form.phone.validPhoneNumber">
        {{$t(this.$getConst.pages.form.phone_field_error)}}.
      </b-form-invalid-feedback>
    </b-form-group>
    <div v-if="isSmsEnabled">
      <b-form-checkbox
        id="sms"
        v-model="form.sms"
        class="mb-2"
        style="z-index: 0 !important;"
        name="sms"
        :value="true"
        :unchecked-value="false"
        switch
      >
        {{this.$t(this.$getConst.pages.sign_up.accept_sms_text)}}.
      </b-form-checkbox>
      <small v-if="!isNumberVerified || isPhoneChanged" class="form-text text-muted">{{$t($getConst.pages.personalDetails.enable_sms_description) | capitalize}}</small>
    </div>

    <div class="text-center py-3" v-if="isSmsEnabled">
      <b-button
        v-if="(isNumberVerified && defaultPhone === form.phone) || (!isPhoneChanged && form.phone)"
                variant="success"
                disabled
      >
        {{$t($getConst.pages.sign_up.phone_verified) | capitalize}}<i :class="[this.$getConst.icons.check, 'ml-2']"/>
      </b-button>
      <div v-else-if="isCodeSent && this.phoneToVerify === this.form.phone">
        <p>{{$t($getConst.pages.personalDetails.verification_code_description) | capitalize}}</p>
        <div class="input-wrapper text-center">
          <PincodeInput
            v-model="code"
            :disabled="isLoading"
            :length="6"
            placeholder="#"
          />
        </div>
        <b-form-invalid-feedback class="d-block text-center" v-if="error">{{error}}</b-form-invalid-feedback>
        <AppConfirmButtons
          no-cancel
          submit-text="verify"
          :is-disabled="code.length !== 6"
          :is-loading="isLoading"
          @onSubmit="updatePhone"
        />
      </div>
      <b-button
        v-else-if="!$v.form.phone.$error && form.phone"
        class="my-auto"
        size="lg"
       :disabled="$v.form.phone.$invalid"
        @click="sendVerificationCode"
      >
        {{$t($getConst.pages.personalDetails.verify_your_phone) | capitalize}}
      </b-button>

    </div>

  </b-overlay>
</template>

<script>
  import jwtDecode from "jwt-decode";
  import VuePhoneNumberInput from 'vue-phone-number-input';
  import requiredIf from "vuelidate/lib/validators/requiredIf";
  import AppFormFeedback from "../../components/common/form/AppFormFeedback";
  import AppSpinner from "../../components/common/utils/AppSpinner";
  import {sendVerificationCodeForSignUp, updatePhoneForSignUp} from "../../api/userPhone.api";
  import PincodeInput from 'vue-pincode-input';
  import AppConfirmButtons from "../../components/common/utils/AppConfirmButtons";
  import parsePhoneNumber from 'libphonenumber-js'
  import {getAllowedMobileCountries} from "../../api/scopes.api";

  export default {
    name: "StepTwo",
    components: {AppConfirmButtons, AppSpinner, AppFormFeedback, VuePhoneNumberInput, PincodeInput},
    computed: {
      isPhoneChanged() {
        return this.defaultPhone !== this.form.phone;
      }
    },
    data() {
      return {
        isCodeSent: false,
        isNumberVerified: false,
        isSmsEnabled: false,
        isLoading: false,
        phoneToVerify: null,
        validNumber: true,
        phoneBasicNumber: null,
        defaultPhone: null,
        isLoadingCountries: false,
        allowedCountries: [],
        translation: {
          countrySelectorLabel: '',
          countrySelectorError: '',
          phoneNumberLabel: this.$t(this.$getConst.pages.personalDetails.phone_number),
          example: ''
        },
        userId: null,
        form: {
          phone: null,
          sms: false,
        },
        error: null,
        code: ""
      }
    },
    validations: {
      phoneBasicNumber: {required: requiredIf(function(model) {return this.form.sms && this.isSmsEnabled}) ,validPhoneNumber() {return this.validNumber}},
      form: {
        phone: {required: requiredIf(function(model) {return this.form.sms && this.isSmsEnabled}) ,validPhoneNumber() {return this.validNumber}},
      }
    },
    methods: {
      getDataFromToken() {
        const tokenDecoded = jwtDecode(this.$route.query.token_hint);
        this.form.phone = tokenDecoded.phone || null;
        this.phoneBasicNumber = tokenDecoded.phone || null;
        this.userId = tokenDecoded.id;
        if (tokenDecoded.phone) {
          this.isNumberVerified = true;
          this.defaultPhone = parsePhoneNumber(tokenDecoded.phone).number;
        }
      },
      showPayload (payload) {
        this.form.phone = "formattedNumber" in payload ? payload.formattedNumber : null;
        this.validNumber = payload.isValid ? true : (this.form.phone === "" || this.form.phone === null);
      },
      sendVerificationCode() {
        this.isLoading = true;
        sendVerificationCodeForSignUp(this.form.phone, this.userId).then(
          (response) => {
            this.isLoading = false;
            this.isCodeSent = true;
            this.phoneToVerify = response.data.phone;
          },
          (error) => {
            this.handleApiError(error);
            this.isLoading = false;
          }
        )
      },
      updatePhone() {
        this.isLoading = true;
        this.error = null;
        updatePhoneForSignUp(this.phoneToVerify, this.code, this.userId).then(
          () => {
            this.defaultPhone = parsePhoneNumber(this.phoneToVerify).number;
            this.form.sms = true;
            this.isNumberVerified = true;
            this.isCodeSent = false;
            this.phoneToVerify = null;
            this.form.sms = true;
            this.$store.dispatch("signup/setSecondStep", this.form);
            this.isLoading = false;
            this.$emit('can-continue', {value: true});
          },
          (error) => {
            if (error.response.status === 404) {
              this.$snotify.error(error.response.data.error);
            } else if(error.response.status === 403 || error.response.status === 422) {
              this.error = error.response.data.error;
            }  else {
              this.$snotify.error(this.$t(this.$getConst.pages.alerts.unknown_error));
            }
            this.isNumberVerified = false;
            this.isLoading = false;
          }
        )
      },
      getAllowedCountries() {
        this.isLoadingCountries = true;
        getAllowedMobileCountries().then(
          (response) => {
            this.allowedCountries = response.data.countries;
            this.isSmsEnabled = response.data.isSmsEnabled;
            this.isLoadingCountries = false;
            this.form.sms = this.isSmsEnabled?true:false;
            this.$v.$touch()
          },
          () => {
            this.isLoadingCountries = false;
          }
        )
      },
      checkFormValidation(condition, val) {
          if(!val.$invalid && !this.isLoadingCountries) {
            this.$store.dispatch("signup/setSecondStep", this.form);
            if (condition) {
              this.$emit('can-continue', {value: true});
            } else {
             this.$emit('can-continue', {value: false});
            }
          } else {
            this.$emit('can-continue', {value: false});

          }
        }
    },
    watch: {
      $v: {
        handler: function (val) {
          let condition = this.form.phone === null || this.form.phone === "" || (this.isNumberVerified && !this.isPhoneChanged) || !this.isSmsEnabled;
          this.checkFormValidation(condition, val);
        },
        deep: true
      },
      clickedNext(val) {
        if(val === true) {
          this.$v.form.$touch();
        }
      }
    },
    mounted() {
      this.getAllowedCountries();
      this.getDataFromToken();
      let condition = this.form.phone === null || this.form.phone === "" || this.isNumberVerified || !this.isSmsEnabled;
      this.checkFormValidation(condition, this.$v);


    }
  }
</script>

<style scoped>

</style>
